import React, { useEffect, useRef, useState } from 'react';
import ReactApexChart from 'react-apexcharts';
import styles from './UserActivities.module.scss';
import { dashboardEndpoints } from 'hooks/endpoints';
import { useFetch } from 'hooks/useFetch';
import moment from 'moment';
import SvgWithBranding from 'component/hoc/branding/withBrandingColor.hoc';
import Tooltip from 'component/Custom/tooltip/Tooltip';
import * as XLSX from 'xlsx';

const hexToRgb = (hex) => {
  hex = hex.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  return { r, g, b };
};

const getDynamicColorScale = (data, brandingData) => {
  if (!data || data.length === 0) return [];

  const allValues = data
    .flatMap((series) => series.data)
    .filter((value) => typeof value === 'number');
  if (allValues.length === 0) return [];

  const minValue = Math.min(...allValues);
  const maxValue = Math.max(...allValues);
  if (minValue === maxValue)
    return [
      {
        from: minValue,
        to: maxValue,
        color: brandingData ? brandingData.page_ButtonColor : '#0744E1',
        foreColor: 'rgba(0,0,0,0)',
      },
    ];

  return [
    {
      from: minValue,
      to: minValue + (maxValue - minValue) * 0.25,
      color: '#ADE8F4',
      foreColor: 'rgba(0,0,0,0)',
    },
    {
      from: minValue + (maxValue - minValue) * 0.25,
      to: minValue + (maxValue - minValue) * 0.5,
      color: '#00B4D8',
      foreColor: 'rgba(0,0,0,0)',
    },
    {
      from: minValue + (maxValue - minValue) * 0.5,
      to: minValue + (maxValue - minValue) * 0.75,
      color: '#0077B6',
      foreColor: 'rgba(0,0,0,0)',
    },
    {
      from: minValue + (maxValue - minValue) * 0.75,
      to: maxValue,
      color: '#020391',
      foreColor: 'rgba(0,0,0,0)',
    },
  ];
};

const roundHeatmapCells = (ctx, r = 4, h = 14) => {
  const el = ctx?.el;
  if (!el) return;
  el.querySelectorAll('.apexcharts-heatmap-rect').forEach((rect) => {
    rect.setAttribute('rx', r);
    rect.setAttribute('ry', r);
    rect.setAttribute('height', h);
  });
};

const UserActivities = ({ id, brandingData, startDate, endDate }) => {
  const chartRef = useRef(null);
  const [chartSeries, setChartSeries] = useState([]);

  const dayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const { data, refetch } = useFetch(
    ['users-activities', startDate, endDate, id],
    dashboardEndpoints.GetUserActivityPerDayHourly(startDate, endDate, id),
    { enabled: false }
  );

  useEffect(() => {
    refetch();
  }, [startDate, endDate, refetch]);

  // NEW: flat table export (Date, Day, Hour, Users)
  const handleExportXLS = () => {
    if (!chartSeries.length) return;

    // helper to pick a date matching the weekday in the selected range
    const pickDateForDay = (dayIndex) => {
      if (!startDate || !endDate) return '';
      const s = moment(startDate);
      const e = moment(endDate);
      const d = s.clone();
      while (d.isSameOrBefore(e)) {
        if (d.day() === dayIndex) return d.format('YYYY-MM-DD'); // matches screenshot
        d.add(1, 'day');
      }
      return '';
    };

    const rows = [];
    for (let dayIdx = 0; dayIdx < 7; dayIdx++) {
      const dateStr = pickDateForDay(dayIdx);
      const dayName = dayLabels[dayIdx];
      // keep hours top-to-bottom as shown in the heatmap (already reversed in state)
      chartSeries.forEach((s) => {
        rows.push({
          Date: dateStr,
          Day: dayName,
          Hour: s.name, // e.g., "12 AM"
          Users: s.data?.[dayIdx] ?? 0,
        });
      });
    }

    const ws = XLSX.utils.json_to_sheet(rows, {
      header: ['Date', 'Day', 'Hour', 'Users'],
    });

    // Optional: widen columns for readability
    ws['!cols'] = [{ wch: 12 }, { wch: 8 }, { wch: 8 }, { wch: 8 }];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'User Activity');

    const filename =
      'user_activity_table_' +
      (startDate ? moment(startDate).format('YYYYMMDD') : 'start') +
      '_' +
      (endDate ? moment(endDate).format('YYYYMMDD') : 'end') +
      '.xlsx';

    XLSX.writeFile(wb, filename);
  };

  useEffect(() => {
    if (data?.data?.length) {
      const formattedData = data.data.map((entry) => ({
        name: moment({ hour: entry.byHour }).format('h A'),
        data: [
          entry.sun,
          entry.mon,
          entry.tue,
          entry.wed,
          entry.thu,
          entry.fri,
          entry.sat,
        ].map((val) => val || 0),
      }));
      setChartSeries([...formattedData].reverse());
    } else {
      setChartSeries([]);
    }
  }, [data]);

  const colorScale = getDynamicColorScale(chartSeries, brandingData);

  const getDateLabelForDay = (dayIndex) => {
    if (!startDate || !endDate) return '';
    const s = moment(startDate);
    const e = moment(endDate);
    const d = s.clone();
    while (d.isSameOrBefore(e)) {
      if (d.day() === dayIndex) return d.format('DD/MM/YYYY');
      d.add(1, 'day');
    }
    return '';
  };

  const chartSettings = {
    series: chartSeries,
    options: {
      chart: {
        id: 'user-activities-chart',
        type: 'heatmap',
        height: 350,
        toolbar: { show: false },
        background: '#FFFFFF',
        zoom: { enabled: false },
        events: {
          mounted: (ctx) =>
            requestAnimationFrame(() => roundHeatmapCells(ctx, 6, 14)),
          updated: (ctx) =>
            requestAnimationFrame(() => roundHeatmapCells(ctx, 6, 14)),
          dataPointSelection: undefined,
        },
      },
      plotOptions: {
        heatmap: {
          dataLabels: { enabled: false },
          colorScale: { ranges: colorScale },
          distributed: false,
          useFillColorAsStroke: false,
        },
      },
      xaxis: {
        categories: dayLabels,
        axisTicks: { show: false },
        axisBorder: { show: false },
        labels: {
          style: {
            fontSize: '12px',
            fontFamily: 'Poppins, sans-serif',
            fontWeight: 400,
            colors: '#000',
          },
        },
      },
      yaxis: {
        labels: {
          style: {
            fontSize: '10px',
            color: '#000',
            fontWeight: 400,
            fontFamily: 'Poppins, sans-serif',
          },
          formatter: (value, index) => (index % 2 !== 0 ? value : ''),
          offsetX: -12,
        },
      },
      grid: {
        show: true,
        borderColor: '#fff',
        padding: { top: 4, right: 4, bottom: 4, left: 4 },
      },
      tooltip: {
        enabled: true,
        custom: ({ series, seriesIndex, dataPointIndex, w }) => {
          const value = series?.[seriesIndex]?.[dataPointIndex] ?? 0;
          const day = w?.config?.xaxis?.categories?.[dataPointIndex] ?? '';
          const hour = w?.config?.series?.[seriesIndex]?.name ?? '';
          const date = getDateLabelForDay(dataPointIndex);
          const c = styles;

          return `
            <div class="${c.uaCard}">
              <div class="${c.uaHdr}">Users</div>
              <div class="${c.uaValue}">${value}</div>
              <div class="${c.uaMeta}">
                <span class="${c.uaChip}">${day}</span>
                ${
                  date
                    ? `<span class="${c.uaSep}"></span><span class="${c.uaChip}">${date}</span>`
                    : ''
                }
                <span class="${c.uaSep}"></span><span class="${
            c.uaChip
          }">${hour}</span>
              </div>
            </div>
          `;
        },
      },

      legend: { show: false },
      stroke: { width: 4, colors: ['#fff'] },
      states: {
        normal: { filter: { type: 'none', value: 0 } },
        hover: { filter: { type: 'lighten', value: 0.1 } },
        active: { filter: { type: 'none', value: 0 } },
      },
    },
  };

  return (
    <div ref={chartRef} className={styles['chart-container']}>
      <div className={styles['actions-container']}>
        <div className={styles['title-container']}>
          <span className={styles['title']}>User Engagement by Hour & Day</span>
          <Tooltip
            content='Displays when users are most active,
helping to identify peak hours or days.'
            className={styles['tooltip']}
          >
            <SvgWithBranding type='info' color='#0744E1' width={22} />
          </Tooltip>
        </div>
        {chartSeries.length === 0 ? (
          <Tooltip content='No data available to export'>
            <SvgWithBranding
              className={styles['download-icon'] + ' ' + styles['disabled']}
              type='download2'
              onClick={() => {}}
            />
          </Tooltip>
        ) : (
          <SvgWithBranding
            className={styles['download-icon']}
            type='download2'
            onClick={handleExportXLS}
          />
        )}
      </div>
      <ReactApexChart
        options={chartSettings.options}
        series={chartSettings.series}
        type='heatmap'
        height={470}
      />
    </div>
  );
};

export default UserActivities;
